import {call, put, takeLatest} from 'redux-saga/effects';
import ApiUtil from "src/utils/ApiUtil";
import {
    BULK_PRODUCTS_QUERY_START,
    BulkProductsReportQueryStartAction,
    getBulkProductsQueryFailedAction,
    getBulkProductsQuerySuccessAction
} from "src/actions/BulkReportQueryAction";
import {ProductInfo, ProgramInput} from "src/model/ProductMatch";
import {
    BatchFeeEstimationResponse,
    CommonInput,
    FeeEstimateRequest,
    InboundingFeeParam,
    MFNProgramInput,
    NonMFNProgramInput,
    ProgramParam
} from "src/model/BulkReport";
import QueryProductInfo from "src/model/QueryProduct";
import {getCurrencyUnitConfig, PROGRAM_NAME_OF_MFN} from "src/config/Config";
import MetricsUtil from "src/utils/MetricsUtil";
import {BATCH_CALL_SIZE} from "src/model/TemplateUploadInputRecord";
import {FeeCategoryListResponse} from "src/model/FeeCategoryList";


export function* getBulkProductReport(action: BulkProductsReportQueryStartAction) {
    try {
        const feeCatParameters: any = {locale: action.locale, countryCode: action.countryCode};
        // @ts-ignore
        const feeCatResponse = yield call(ApiUtil.callApiWithTimeout, "getfeecategorylist", feeCatParameters);
        if (feeCatResponse.succeed) {
            let products: Array<ProductInfo> = action.products;
            const result = new Array<Array<string>>();
            let metricsUtil = MetricsUtil.getMetricsUtil();
            if (metricsUtil) {
                metricsUtil.publishCounter("getbatchfees", "bulkSize", products.length);
            }
            const batchSize = action.batchSize ?? BATCH_CALL_SIZE;
            //split into small batches to avoid time out
            for (let i = 0; i < products.length; i += batchSize) {
                const chunk = products.slice(i, i + batchSize);
                const parameters = {"feeEstimationRequestList": buildRequest(chunk, action.countryCode, feeCatResponse)};
                // @ts-ignore
                const response: BatchFeeEstimationResponse = yield call(ApiUtil.postApiWithTimeout, "getbatchfees", parameters, ApiUtil.batch_api_call_timeout);
                if (response.succeed) {
                    if (result.length == 0) {
                        result.push(...response.data?.batchFeeEstimationCsvReport);
                    } else {
                        // skip header
                        result.push(...response.data?.batchFeeEstimationCsvReport.slice(2));
                    }
                } else {
                    if (metricsUtil) {
                        metricsUtil.publishCounter("getbatchfees", "failedAt", i);
                    }
                    // TODO: retry sub batch
                    yield put(getBulkProductsQueryFailedAction());
                    return;
                }
            }
            yield put(getBulkProductsQuerySuccessAction(result, action.countryCode));
        } else {
            yield put(getBulkProductsQueryFailedAction());
        }
    } catch (error) {
        console.error("Call API getbatchfees error" + error)
        // @ts-ignore
        yield put(getBulkProductsQueryFailedAction(error));
        return;
    }
}

const buildRequest = (products: Array<ProductInfo>, countryCode: string, feeCatResponse: FeeCategoryListResponse) => {
    let feeEstimateRequests: Array<FeeEstimateRequest> = [];
    // @ts-ignore
    const categoryToTrexIDMap = Object.fromEntries(Object.entries(feeCatResponse.trexIDToFeeCategoryMap).map(([k, v]) => [v, k]));
    products.forEach(product => {
        let programList: Array<string> = [];
        let programParamMap: Record<string, ProgramParam> = {};
        const inputMap = product.programInputMap as Map<string, ProgramInput>;
        inputMap.forEach((programInput, programParamIdentifier) => {
            programList.push(programParamIdentifier);
            programParamMap = {
                ...programParamMap,
                [programParamIdentifier]: buildProgramParam(programParamIdentifier, programInput, countryCode)
            };
        })
        const feeEstimateRequest: FeeEstimateRequest = {
            countryCode: countryCode,
            itemInfo: productInfoToQueryProduct(product, countryCode, categoryToTrexIDMap),
            programIdList: programList,
            programParamMap: programParamMap,
            isNonPeakSelected: product.isNonPeakSelected ?? true
        };
        feeEstimateRequests.push(feeEstimateRequest);
    })

    return feeEstimateRequests;
}

const buildProgramParam = (programParamIdentifier: string, programInput: ProgramInput, countryCode: string) => {
    // Now keys in the program param map / program input map are all program param identifier and are not necessarily related to
    // program. program field in the program input is a more authoritative source. Only fall back to programParamIdentifier.
    const cleanProgramName = programInput.program ?? programParamIdentifier;
    const commonInput: CommonInput = {
        averageInventoryUnitsStored: programInput.averageInventoryUnitsStored ?? 1,
        costOfGoodsSold: programInput.costOfGoodsSold ?? 0,
        estimatedMonthlyUnitsSold: programInput.estimatedMonthlyUnitsSold ?? 1,
        estimatesSales: programInput.estimatedSales ?? 1,
        miscellaneousCost: programInput.miscellaneousCost ?? 0
    }
    const mfnProgramInput: MFNProgramInput = {
        fulfillmentCost: programInput.fulfillCost ?? 0,
        monthlyStorageFeePerUnit: programInput.monthlyStorageCostPerUnit ?? 0,
        shippingCharge: programInput.shippingCharge ?? 0
    }
    const ipsFeeParam: InboundingFeeParam | undefined = programInput.ipsOption != null ? {
        // We set the default values in bulk search component if program/country is eligible for IPS. So here if the value is undefined,
        // we set it to null so backend won't pick up the parameter.
        serviceOption: programInput.ipsOption ?? null,
        regionLocationsMap: programInput.ipsRegion ? {[programInput.ipsRegion]: programInput.ipsLocationNum ?? 1} : null,
        unitToInbound: programInput.unitToInbound ?? null,
    } : undefined;

    const nonMFNProgramInput: NonMFNProgramInput = {
        isBubblewrapChosen: programInput.isBubbleWrap ?? false,
        isFBAInboundConvenienceChosen: programInput.isPlacement ?? false,
        isLabelingChosen: programInput.isLabel ?? false,
        isOpaqueBaggingChosen: programInput.isOpaqueBagging ?? false,
        isPolybaggingChosen: programInput.isBagging ?? false,
        isTapingChosen: programInput.isTape ?? false,
        shippingToAmazon: programInput.sendToAmazon ?? 0,
    }

    let programParam: ProgramParam = {
        program: cleanProgramName,
        inventoryPlace: programInput.inventoryPlace ?? countryCode,
        marketplace: programInput.marketplace ?? countryCode,
        commonInput: commonInput,
        inboundingFeeParam: ipsFeeParam,
    }
    programParam = cleanProgramName == PROGRAM_NAME_OF_MFN ?
        {
            ...programParam,
            mfnProgramInput: mfnProgramInput
        } : {
            ...programParam,
            nonMFNProgramInput: nonMFNProgramInput
        };
    return programParam;
}

const productInfoToQueryProduct = (product: ProductInfo, countryCode: string, feeCatResponse: Record<string, string>) => {
    let queryProduct: QueryProductInfo = {
        asin: product.asin,
        merchantSku: product.merchantSku,
        fnsku: product.fnsku,
        glProductGroupName: product.gl,
        // @ts-ignore
        tRexId: feeCatResponse[product.feeCategory] ? feeCatResponse[product.feeCategory] : '-1',
        feeCategory: product.feeCategory,
        specialDeliveryRequirement: product.specialDeliveryRequirement,
        title: product.title,
        packageLength: product.length + '',
        packageWidth: product.width + '',
        packageHeight: product.height + '',
        dimensionUnit: product.dimensionUnit!,
        packageWeight: product.weight + '',
        weightUnit: product.weightUnit!,
        afnPriceStr: product.price + '',// User can update them in program component
        mfnPriceStr: product.price + '',
        mfnShippingPriceStr: '0',//TODO:get shipping from MYI
        currency: product.currency ? product.currency : getCurrencyUnitConfig(countryCode),
        isNewDefined: false,
    };
    return queryProduct;
}

export default function* bulkProductReport() {
    yield takeLatest([BULK_PRODUCTS_QUERY_START], getBulkProductReport);
}
